/**
 * Domina Agent — a closed, hard-wired brain. NO API calls, NO LLM, NO tokens.
 *
 * She matches what the user types against a curated set of intents (keywords +
 * phrases), tolerant of typos and common misspellings, and replies in Domina's
 * natural, warm voice with action buttons (deep-links, follow-up chips, "talk
 * to a human", "switch to Domina AI", "take a tour"). Everything here runs in
 * the browser — it costs nothing and answers instantly.
 *
 * Expanding her knowledge = add an Intent below. Keep answers short + friendly.
 */

export interface AgentAction {
  label: string;
  /** Navigate to an in-app route (closes the widget). */
  to?: string;
  /** External link / mailto. */
  href?: string;
  /** Re-ask the Agent with this text (a follow-up chip). */
  ask?: string;
  /** Special actions handled by the UI. */
  kind?: 'human' | 'ai' | 'tour' | 'tour-next';
  /** For 'tour-next': which tour step to show next (makes Next reload-proof). */
  stepIndex?: number;
}

export interface AgentReply {
  /** Markdown-lite text (bold, lists, links supported by MarkdownLite). */
  text: string;
  actions?: AgentAction[];
  /** false → the UI also offers "talk to a human" / "ask Domina AI". */
  confident: boolean;
}

interface Intent {
  id: string;
  /** Canonical trigger words (single words matched fuzzily; phrases matched as substrings). */
  keywords: string[];
  /** Strong multi-word phrases that strongly imply this intent. */
  phrases?: string[];
  reply: AgentReply;
  /** Weight multiplier (default 1) to bias important intents. */
  weight?: number;
}

// ── Normalisation + typo/synonym handling ─────────────────────────────────────

/** Common misspellings / shorthands → canonical word. Applied per token. */
const SYNONYMS: Record<string, string> = {
  // platforms
  insta: 'instagram', ig: 'instagram', instgram: 'instagram', instragram: 'instagram',
  fb: 'facebook', facebok: 'facebook', facbook: 'facebook', faceboook: 'facebook',
  x: 'twitter', tweet: 'twitter', tweets: 'twitter', twiter: 'twitter',
  yt: 'youtube', youtub: 'youtube', utube: 'youtube',
  linkdin: 'linkedin', linkedn: 'linkedin', linkd: 'linkedin',
  tiktok: 'tiktok', tictok: 'tiktok', tikok: 'tiktok',
  gbp: 'google', gmb: 'google', googel: 'google', googlemaps: 'google',
  threads: 'threads', whatsap: 'whatsapp', whatspp: 'whatsapp',
  // features
  calender: 'calendar', calandar: 'calendar', shedule: 'schedule', schedual: 'schedule',
  scheduale: 'schedule', analitics: 'analytics', analitcs: 'analytics', anaytics: 'analytics',
  reportn: 'report', repot: 'report', reprot: 'report',
  mention: 'mentions', listning: 'listening', lisening: 'listening',
  engae: 'engage', engag: 'engage', inbox: 'engage',
  contacts: 'crm', contact: 'crm', leads: 'crm', lead: 'crm', deals: 'crm', pipeline: 'crm',
  pr: 'media', journalist: 'media', journo: 'media', press: 'media',
  bio: 'biolink', linkinbio: 'biolink',
  task: 'tasks', todo: 'tasks', todos: 'tasks', planner: 'tasks',
  project: 'projects',
  seo: 'seo', geo: 'seo',
  post: 'post', posts: 'post', publish: 'publish', publishing: 'publish',
  compose: 'create', write: 'create', draft: 'create',
  team: 'team', colleague: 'team', colleagues: 'team', members: 'team',
  billing: 'billing', invoice: 'billing', payment: 'billing', subscription: 'billing',
  token: 'tokens', credit: 'tokens', credits: 'tokens', topup: 'tokens',
  connect: 'connect', integrate: 'connect', integration: 'connect', integrations: 'connect',
  reconnect: 'connect', link: 'connect',
  settings: 'settings', setting: 'settings', config: 'settings',
  workspace: 'workspace', client: 'workspace', clients: 'workspace',
  mfa: 'mfa', '2fa': 'mfa', authenticator: 'mfa', otp: 'mfa',
  cancel: 'cancel', unsubscribe: 'cancel',
  price: 'pricing', prices: 'pricing', cost: 'pricing', plans: 'pricing', plan: 'pricing',
  help: 'help', support: 'support', human: 'human', person: 'human', agent: 'human',
  domina: 'domina',
  // more features
  approvel: 'approval', aproval: 'approval', aprove: 'approve', approuval: 'approval',
  queue: 'queue', notifcation: 'notifications', notifcations: 'notifications', notification: 'notifications',
  competiter: 'competitor', competetor: 'competitor',
  timezon: 'timezone', timzone: 'timezone', hashtg: 'hashtags', hastag: 'hashtags', hashtag: 'hashtags',
  passwrd: 'password', pasword: 'password', passwort: 'password',
  shorten: 'shorten', shortlink: 'shorten', utm: 'utm',
  scheule: 'schedule', anayltics: 'analytics', analtyics: 'analytics',
  profle: 'profile', avatar: 'profile', darkmode: 'dark',
  // smalltalk
  hi: 'hello', hey: 'hello', howzit: 'hello', hiya: 'hello', yo: 'hello', heya: 'hello',
  thanks: 'thank', thankyou: 'thank', thx: 'thank', ty: 'thank', cheers: 'thank',
};

function normalize(q: string): string {
  return q
    .toLowerCase()
    .replace(/[^a-z0-9\s']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokensOf(q: string): string[] {
  return normalize(q)
    .split(' ')
    .filter(Boolean)
    .map((t) => SYNONYMS[t] ?? t);
}

/** Levenshtein distance (small, for typo tolerance). */
function lev(a: string, b: string): number {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  let curr = new Array<number>(n + 1);
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1]! + 1, prev[j]! + 1, prev[j - 1]! + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n]!;
}

/** Fuzzy token equality — allows 1 edit for short words, 2 for longer ones. */
function fuzzyEq(a: string, b: string): boolean {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 2) return false;
  const tol = a.length <= 4 ? 1 : 2;
  return lev(a, b) <= tol;
}

// ── Reusable action buttons ───────────────────────────────────────────────────

const A = {
  tour: { label: '✨ Take a tour', kind: 'tour' as const },
  human: { label: '🙋 Talk to a human', kind: 'human' as const },
  ai: { label: '👑 Ask Domina AI', kind: 'ai' as const },
};

// ── The knowledge: intents ────────────────────────────────────────────────────

const INTENTS: Intent[] = [
  // — smalltalk —
  {
    id: 'greeting',
    keywords: ['hello'],
    phrases: ['good morning', 'good afternoon', 'good evening'],
    weight: 0.8,
    reply: {
      text: "Howzit! 👋 I'm **Domina Agent** — your free in-app guide. I know my way around every corner of CNTRL, so ask me anything or let me show you around.",
      actions: [A.tour, { label: 'How do I schedule a post?', ask: 'how do I schedule a post' }, { label: 'What can you do?', ask: 'what can you do' }],
      confident: true,
    },
  },
  {
    id: 'thanks',
    keywords: ['thank'],
    weight: 0.8,
    reply: { text: "Any time! 💖 Shout if you need anything else.", confident: true },
  },
  {
    id: 'who',
    keywords: ['who'],
    phrases: ['who are you', 'what are you', 'your name'],
    reply: {
      text: "I'm **Domina** 👑 — your assistant inside CNTRL. There are two of me:\n\n- **Domina Agent** (that's me now) — *free*, I answer questions and show you around, no credits used.\n- **Domina AI** — the clever one who *generates* content, images, reports and analyses your data (uses AI credits).\n\nToggle between us at the top any time.",
      actions: [A.ai, A.tour],
      confident: true,
    },
  },
  {
    id: 'capabilities',
    keywords: ['help', 'capabilities'],
    phrases: ['what can you do', 'what can i do', 'how can you help', 'what do you do', 'what can i do here'],
    reply: {
      text: "Lots! I can explain **anything** in CNTRL and take you straight there. A few favourites:\n\n- 📅 Plan, create & schedule posts\n- 💬 Reply to comments & DMs in **Engage**\n- 👂 Track mentions in **Listening**\n- 📊 See performance in **Analytics** & build **Reports**\n- 🔗 Connect your social accounts\n\nFor *generating* copy, images or reports, switch to **Domina AI**.",
      actions: [A.tour, A.ai, { label: 'Connect an account', ask: 'connect a social account' }],
      confident: true,
    },
  },

  // — switch to AI / generative —
  {
    id: 'generate',
    keywords: ['generate', 'create', 'image', 'caption', 'ai'],
    phrases: ['write me', 'generate a', 'make me', 'create an image', 'write a caption', 'write a post', 'generate a report', 'analyse', 'analyze'],
    reply: {
      text: "That's a job for **Domina AI** 👑 — she generates copy, images and reports and can analyse your data. She uses AI credits (I'm free, but I can't generate). Want me to switch you over?",
      actions: [A.ai, { label: 'What does AI cost?', ask: 'pricing tokens' }],
      confident: true,
    },
  },

  // — nav / features —
  {
    id: 'dashboard',
    keywords: ['dashboard', 'home'],
    reply: {
      text: "Your **Home** greets you and asks what you'd like to work on, with quick action cards (Plan, Create, Schedule, Measure, Grow, Discuss, Engage, Listen) plus your tasks, a glance at your numbers and anything that needs attention.",
      actions: [{ label: 'Go to Home', to: '/dashboard' }],
      confident: true,
    },
  },
  {
    id: 'publish',
    keywords: ['publish', 'plan'],
    phrases: ['content planner', 'plan content', 'plan my posts'],
    reply: {
      text: "**Content Planner** is where you plan and draft everything you'll post. From here you create posts, save drafts and send them for approval.",
      actions: [{ label: 'Open Content Planner', to: '/publish' }, { label: 'Create a post', to: '/calendar?compose=1' }],
      confident: true,
    },
  },
  {
    id: 'create-post',
    keywords: ['create', 'post', 'publish'],
    phrases: ['create a post', 'new post', 'make a post', 'write a post', 'publish a post'],
    weight: 1.2,
    reply: {
      text: "To **create a post**:\n\n1. Open the **Composer**\n2. Pick your networks\n3. Write your caption (or customise per network)\n4. Add media\n5. **Schedule**, **Add to queue** or **Publish now**\n\nWant me to open it?",
      actions: [{ label: 'Open the Composer', to: '/calendar?compose=1' }],
      confident: true,
    },
  },
  {
    id: 'schedule',
    keywords: ['schedule', 'calendar'],
    phrases: ['schedule a post', 'content calendar', 'when will it post', 'plan a date'],
    weight: 1.2,
    reply: {
      text: "You schedule from the **Composer** (pick **Schedule** and set a date/time), then see everything on the **Content Calendar** — drag posts to reschedule, tap one to edit or publish.",
      actions: [{ label: 'Open Calendar', to: '/calendar' }, { label: 'Create & schedule a post', to: '/calendar?compose=1' }],
      confident: true,
    },
  },
  {
    id: 'engage',
    keywords: ['engage', 'comments', 'dms', 'replies', 'reply'],
    phrases: ['reply to comments', 'answer messages', 'direct messages', 'my inbox'],
    reply: {
      text: "**Engage** is your unified inbox — comments, DMs, mentions and reviews from your connected networks in one place, so you can reply without hopping between apps.",
      actions: [{ label: 'Open Engage', to: '/engage' }],
      confident: true,
    },
  },
  {
    id: 'listening',
    keywords: ['listening', 'mentions', 'sentiment', 'monitor'],
    phrases: ['who is talking about', 'brand mentions', 'track mentions', 'social listening'],
    reply: {
      text: "**Listening** tracks what people say about you across the web and social — with sentiment (positive/negative/neutral) and share of voice. Set up a query with your brand + keywords to start.",
      actions: [{ label: 'Open Listening', to: '/listening' }],
      confident: true,
    },
  },
  {
    id: 'seo',
    keywords: ['seo', 'search', 'keywords', 'ranking'],
    phrases: ['search engine', 'google ranking', 'ai visibility', 'keyword ideas'],
    reply: {
      text: "**SEO & GEO** helps you get found — keyword ideas, Google Search Console rank tracking, and **GEO** (whether AI assistants mention your brand when people ask).",
      actions: [{ label: 'Open SEO & GEO', to: '/seo' }],
      confident: true,
    },
  },
  {
    id: 'media-pr',
    keywords: ['media', 'pr', 'press', 'pitch'],
    phrases: ['public relations', 'media list', 'journalists', 'press release', 'pitch a story'],
    reply: {
      text: "**Public Relations** is your media-relations hub — a directory of outlets & journalists, media lists, and a pitch pipeline to track your outreach.",
      actions: [{ label: 'Open Public Relations', to: '/media' }],
      confident: true,
    },
  },
  {
    id: 'media-library',
    keywords: ['medialibrary', 'library', 'assets', 'gallery'],
    phrases: ['media library', 'my images', 'uploaded media'],
    reply: {
      text: "The **Media** library holds everything you've uploaded plus images Domina AI has generated — reuse them in any post.",
      actions: [{ label: 'Open Media', to: '/media-library' }],
      confident: true,
    },
  },
  {
    id: 'projects',
    keywords: ['projects'],
    phrases: ['my projects', 'campaign'],
    reply: {
      text: "**Projects** organise your work into campaigns — group tasks, track progress and keep your team aligned.",
      actions: [{ label: 'Open Projects', to: '/projects' }],
      confident: true,
    },
  },
  {
    id: 'tasks',
    keywords: ['tasks'],
    phrases: ['task planner', 'my to do', 'assign a task', 'whats due'],
    reply: {
      text: "**Task Planner** is your to-do hub — assign tasks to teammates, set due dates, and tick things off. Your open tasks also show on your Home.",
      actions: [{ label: 'Open Task Planner', to: '/planner' }],
      confident: true,
    },
  },
  {
    id: 'crm',
    keywords: ['crm'],
    phrases: ['my contacts', 'add a lead', 'sales pipeline', 'deals', 'quotes'],
    reply: {
      text: "The **CRM** keeps your contacts, leads and deals — move deals through your pipeline and build quotes.",
      actions: [{ label: 'Open CRM', to: '/crm' }],
      confident: true,
    },
  },
  {
    id: 'chat',
    keywords: ['discuss', 'chat'],
    phrases: ['team chat', 'message my team', 'talk to my team'],
    reply: {
      text: "**Team Chat** is where you and your teammates talk — channels and DMs, right inside CNTRL.",
      actions: [{ label: 'Open Team Chat', to: '/chat' }],
      confident: true,
    },
  },
  {
    id: 'analytics',
    keywords: ['analytics', 'performance', 'stats', 'insights'],
    phrases: ['how are my posts doing', 'my numbers', 'followers growth', 'engagement rate'],
    reply: {
      text: "**Analytics** shows how you're doing — followers, reach, engagement and your top posts across every connected network and Google Analytics.",
      actions: [{ label: 'Open Analytics', to: '/analytics' }],
      confident: true,
    },
  },
  {
    id: 'reports',
    keywords: ['reports', 'report'],
    phrases: ['build a report', 'monthly report', 'send a report', 'pdf report'],
    reply: {
      text: "**Reports** turn your data into shareable PDFs — build a template, run it for a date range, and schedule it to email automatically. (Domina AI can write the narrative for you.)",
      actions: [{ label: 'Open Reports', to: '/reports' }, A.ai],
      confident: true,
    },
  },
  {
    id: 'biolink',
    keywords: ['biolink'],
    phrases: ['link in bio', 'bio page', 'bio link'],
    reply: {
      text: "**Link in Bio** gives you one tidy landing page of links for your social profiles — customise it under Settings → Link in Bio.",
      actions: [{ label: 'Open Link in Bio', to: '/settings/bio' }],
      confident: true,
    },
  },

  // — how-tos —
  {
    id: 'connect',
    keywords: ['connect', 'account', 'accounts'],
    phrases: ['connect a social account', 'add an account', 'add account', 'link my instagram', 'reconnect', 'connect facebook', 'connect instagram'],
    weight: 1.3,
    reply: {
      text: "To **connect a social account**: go to **Settings → Integrations**, pick the network and follow the sign-in. If an account says *Reconnect*, its login expired — just reconnect it there.",
      actions: [{ label: 'Open Integrations', to: '/settings/integrations' }],
      confident: true,
    },
  },
  {
    id: 'team',
    keywords: ['team', 'invite'],
    phrases: ['invite a team member', 'add a user', 'add someone', 'invite someone'],
    reply: {
      text: "To **add a teammate**: Settings → Team → invite them by email and pick their role. They'll get an email to join your workspace.",
      actions: [{ label: 'Open Team settings', to: '/settings/team' }],
      confident: true,
    },
  },
  {
    id: 'workspace',
    keywords: ['workspace'],
    phrases: ['add a client', 'new workspace', 'create a workspace', 'client account'],
    reply: {
      text: "Each **workspace** is a separate brand/client. Create one under Settings → Workspace, then connect that client's accounts inside it. Switch workspaces from the switcher at the bottom of the rail.",
      actions: [{ label: 'Workspace settings', to: '/settings/workspace' }],
      confident: true,
    },
  },
  {
    id: 'billing',
    keywords: ['billing', 'tokens'],
    phrases: ['buy tokens', 'top up', 'my plan', 'invoice', 'upgrade'],
    reply: {
      text: "**Billing** is under Settings → Billing — see your plan, buy AI token packs (for Domina AI) and manage your subscription.",
      actions: [{ label: 'Open Billing', to: '/settings/billing' }],
      confident: true,
    },
  },
  {
    id: 'pricing',
    keywords: ['pricing'],
    phrases: ['how much', 'what does it cost', 'what do tokens cost'],
    reply: {
      text: "I'm **free** — Domina Agent never costs a thing. **Domina AI** uses token credits for generating content/images/reports; you can see your balance and buy packs under Billing. For plan pricing, our team can give you the exact numbers.",
      actions: [{ label: 'Open Billing', to: '/settings/billing' }, A.human],
      confident: true,
    },
  },
  {
    id: 'mfa',
    keywords: ['mfa'],
    phrases: ['two factor', 'authenticator app', 'secure my account', 'set up 2fa'],
    reply: {
      text: "Turn on **two-factor authentication** under Settings → Security — scan the QR with an authenticator app and save your backup codes. Paid plans require it (with a grace period).",
      actions: [{ label: 'Open Security settings', to: '/settings/security' }],
      confident: true,
    },
  },
  {
    id: 'cancel',
    keywords: ['cancel'],
    phrases: ['cancel my subscription', 'close my account', 'stop paying'],
    reply: {
      text: "You can **cancel** under Settings → Billing — it stays active until the end of your current period. If you'd rather talk it through first, I can put you onto a human.",
      actions: [{ label: 'Open Billing', to: '/settings/billing' }, A.human],
      confident: true,
    },
  },
  {
    id: 'settings',
    keywords: ['settings'],
    reply: {
      text: "**Settings** covers your profile, workspace, team, integrations, billing, security and more. What would you like to change?",
      actions: [{ label: 'Open Settings', to: '/settings' }, { label: 'Integrations', to: '/settings/integrations' }, { label: 'Billing', to: '/settings/billing' }],
      confident: true,
    },
  },

  // — more how-tos & features —
  {
    id: 'about',
    keywords: ['about', 'cntrl'],
    phrases: ['what is cntrl', 'what is this', 'what is iheart', 'tell me about cntrl', 'what does this app do'],
    reply: {
      text: "**iHeart CNTRL** is your all-in-one home for social media, PR and reporting — plan & schedule content, reply to your audience, track mentions, measure performance and build reports, all in one place. Want the quick tour?",
      actions: [A.tour],
      confident: true,
    },
  },
  {
    id: 'approvals',
    keywords: ['approval', 'approve'],
    phrases: ['approve a post', 'approval workflow', 'needs approval', 'send for approval', 'review a post'],
    reply: {
      text: "Posts can need **approval** before they go out. A reviewer approves (or requests changes) from the Content Planner. If something's waiting on you, it shows in **Needs attention** on your Home.",
      actions: [{ label: 'Open Content Planner', to: '/publish' }],
      confident: true,
    },
  },
  {
    id: 'queue',
    keywords: ['queue', 'slots'],
    phrases: ['add to queue', 'posting queue', 'queue schedule', 'best time to post', 'automatic times'],
    reply: {
      text: "The **queue** drops your posts into pre-set time slots automatically — choose **Add to queue** in the Composer, and set your slot times in Settings → Queue Schedule.",
      actions: [{ label: 'Queue settings', to: '/settings/queue' }, { label: 'Create a post', to: '/calendar?compose=1' }],
      confident: true,
    },
  },
  {
    id: 'bulk',
    keywords: ['bulk'],
    phrases: ['bulk schedule', 'bulk upload', 'content plan', 'mass schedule', 'upload a csv', 'lots of posts', 'many posts at once'],
    reply: {
      text: "To schedule lots of posts at once, use **Bulk / Content Plans** — write or paste dated content (or a CSV) and mass-schedule it straight into the publisher.",
      actions: [{ label: 'Open Bulk', to: '/publish/bulk' }, { label: 'Content Plans', to: '/publish/plans' }],
      confident: true,
    },
  },
  {
    id: 'first-comment',
    keywords: ['firstcomment'],
    phrases: ['first comment', 'comment after posting', 'hashtags in a comment', 'post a comment after'],
    reply: {
      text: "Set a **first comment** to auto-post right after your post goes live (great for hashtags) — it's in the Composer's per-network options, for Facebook, Instagram & LinkedIn.",
      actions: [{ label: 'Create a post', to: '/calendar?compose=1' }],
      confident: true,
    },
  },
  {
    id: 'hashtags',
    keywords: ['hashtags'],
    phrases: ['add hashtags', 'suggest hashtags', 'what hashtags', 'hashtag ideas'],
    reply: {
      text: "For hashtag ideas, let **Domina AI** suggest them per network in the Composer's AI Assist panel — and pop them in a **first comment** to keep your caption clean.",
      actions: [A.ai, { label: 'Create a post', to: '/calendar?compose=1' }],
      confident: true,
    },
  },
  {
    id: 'per-network',
    keywords: ['customise', 'customize'],
    phrases: ['per network', 'customise per network', 'different caption', 'different for each network', 'tweak per platform'],
    reply: {
      text: "Turn on **Customise for each network** in the Composer to give every platform its own caption and media — handy when the same post needs a different tone on LinkedIn vs Instagram.",
      actions: [{ label: 'Create a post', to: '/calendar?compose=1' }],
      confident: true,
    },
  },
  {
    id: 'media-specs',
    keywords: ['sizes', 'dimensions', 'format', 'requirements', 'video'],
    phrases: ['image size', 'video length', 'what size', 'file size', 'image requirements', 'video requirements'],
    reply: {
      text: "Images: JPEG / PNG / WebP up to 50 MB. Video: MP4 / WebM up to 512 MB. Instagram needs at least one image. Each network's own limits are checked before you publish — I'll flag anything that won't fit.",
      confident: true,
    },
  },
  {
    id: 'failed-post',
    keywords: ['failed', 'fail', 'error'],
    phrases: ['post failed', 'why did my post fail', 'it didnt post', 'failed to publish', 'publishing error', 'post didnt go out'],
    reply: {
      text: "A **failed** post usually means the account needs reconnecting, the caption went over a network's limit, or the media didn't meet the platform's rules. Check **Needs attention** on your Home and open the post on the Calendar for the exact reason.",
      actions: [{ label: 'Open Calendar', to: '/calendar' }, { label: 'Integrations', to: '/settings/integrations' }],
      confident: false,
    },
  },
  {
    id: 'edit-delete-live',
    keywords: ['delete', 'edit', 'unpublish', 'takedown'],
    phrases: ['delete a post', 'edit a published post', 'delete a published post', 'remove a post', 'take down a post', 'delete a live post'],
    weight: 1.2,
    reply: {
      text: "For a post that's already live, open it on the **Calendar** and use the actions panel to **edit** or **delete** it — where the network allows (e.g. Instagram can't edit a caption via its API). I show exactly what each platform supports.",
      actions: [{ label: 'Open Calendar', to: '/calendar' }],
      confident: true,
    },
  },
  {
    id: 'notifications',
    keywords: ['notifications', 'alerts'],
    phrases: ['manage notifications', 'turn off emails', 'notification settings', 'stop emails'],
    reply: {
      text: "Manage what you're notified about (in-app, email and more) under Settings → Notifications.",
      actions: [{ label: 'Notification settings', to: '/settings/notifications' }],
      confident: true,
    },
  },
  {
    id: 'shortcuts',
    keywords: ['shortcut', 'shortcuts'],
    phrases: ['keyboard shortcut', 'command palette', 'search everything', 'quick search'],
    reply: {
      text: "Press **⌘K** (Ctrl+K on Windows) anywhere to open the command palette and jump straight to anything.",
      confident: true,
    },
  },
  {
    id: 'export',
    keywords: ['export'],
    phrases: ['export my data', 'download my data', 'get my content', 'export content'],
    reply: {
      text: "Export everything you've created as a CSV from Settings → your profile (Export my content).",
      actions: [{ label: 'Open Settings', to: '/settings' }],
      confident: true,
    },
  },
  {
    id: 'password',
    keywords: ['password'],
    phrases: ['change my password', 'reset password', 'update password'],
    reply: {
      text: "Change your password under Settings → Security. Forgot it? Sign out and use 'Forgot password' on the login screen.",
      actions: [{ label: 'Open Security', to: '/settings/security' }],
      confident: true,
    },
  },
  {
    id: 'timezone',
    keywords: ['timezone'],
    phrases: ['my time zone', 'set timezone', 'wrong time', 'scheduling time'],
    reply: {
      text: "Set your default **time zone** under Settings → Profile so scheduled times match your day.",
      actions: [{ label: 'Open Settings', to: '/settings' }],
      confident: true,
    },
  },
  {
    id: 'profile',
    keywords: ['profile'],
    phrases: ['my profile', 'edit my profile', 'change my name', 'profile picture', 'update my photo'],
    reply: {
      text: "Update your name, photo, bio and time zone under Settings → Profile.",
      actions: [{ label: 'Open Settings', to: '/settings' }],
      confident: true,
    },
  },
  {
    id: 'gbp',
    keywords: ['gbp'],
    phrases: ['google business', 'google my business', 'google maps post', 'business profile'],
    reply: {
      text: "Once connected, you can post to and manage your **Google Business Profile** (name, phone, website, hours, description) from Settings → Integrations, and publish Google posts from the Composer.",
      actions: [{ label: 'Integrations', to: '/settings/integrations' }],
      confident: true,
    },
  },
  {
    id: 'report-schedule',
    keywords: ['scheduledreport'],
    phrases: ['schedule a report', 'email a report', 'automatic report', 'monthly report', 'send report automatically'],
    reply: {
      text: "Build a template in **Reports**, then schedule it to generate and **email** automatically on your chosen cadence. Domina AI can write the narrative for you.",
      actions: [{ label: 'Open Reports', to: '/reports' }, A.ai],
      confident: true,
    },
  },
  {
    id: 'competitors',
    keywords: ['competitor', 'benchmark'],
    phrases: ['competitor analysis', 'compare to competitors', 'how do i compare'],
    reply: {
      text: "The **Competitor Analysis** tab under Analytics lets you benchmark your Instagram & YouTube against others.",
      actions: [{ label: 'Open Analytics', to: '/analytics' }],
      confident: true,
    },
  },
  {
    id: 'shortlinks',
    keywords: ['shorten', 'utm'],
    phrases: ['short link', 'track clicks', 'utm tracking', 'link clicks', 'shorten a link'],
    reply: {
      text: "Paste a link in the Composer and CNTRL auto-shortens it (ihrt.link) with click tracking + UTM support. See the clicks in Analytics → Short Links & UTM.",
      actions: [{ label: 'Short links & UTM', to: '/analytics' }],
      confident: true,
    },
  },
  {
    id: 'theme',
    keywords: ['theme', 'dark'],
    phrases: ['dark mode', 'light mode', 'change theme', 'night mode'],
    reply: {
      text: "CNTRL runs in a light, on-brand theme for now — a dark mode isn't available yet, but I'll pass the request along! 🌙",
      confident: true,
    },
  },
  {
    id: 'customise-home',
    keywords: ['rearrange', 'reorder'],
    phrases: ['customise my home', 'customise dashboard', 'move cards', 'hide a card', 'rearrange my dashboard'],
    reply: {
      text: "On your Home, tap **Customise** (top-right) to drag cards into the order you like, hide what you don't use, and add them back. Your layout saves to your account.",
      actions: [{ label: 'Go to Home', to: '/dashboard' }],
      confident: true,
    },
  },
  {
    id: 'switch-workspace',
    keywords: ['switch'],
    phrases: ['switch workspace', 'change workspace', 'another client', 'switch client', 'change client'],
    reply: {
      text: "Switch between your workspaces (clients) from the workspace switcher at the bottom of the left menu — each has its own connected accounts and content.",
      confident: true,
    },
  },
  {
    id: 'delete-account',
    keywords: [],
    phrases: ['delete my account', 'close my account', 'delete account', 'remove my account'],
    reply: {
      text: "You can delete your own account under Settings → Profile. Your clients' content stays safe — it's transferred within each workspace. Want to talk to a human first?",
      actions: [{ label: 'Open Settings', to: '/settings' }, A.human],
      confident: true,
    },
  },

  // — escalation —
  {
    id: 'human',
    keywords: ['human', 'support'],
    phrases: ['talk to a human', 'speak to someone', 'contact support', 'real person', 'help desk'],
    weight: 1.2,
    reply: {
      text: "Of course — I'll connect you with a real person. You can message your workspace admin here, or email our team.",
      actions: [A.human, { label: '✉️ Email support', href: 'mailto:hello@iheartdigital.co.za' }],
      confident: true,
    },
  },
  {
    id: 'tour',
    keywords: ['tour', 'show', 'guide', 'walkthrough'],
    phrases: ['take a tour', 'show me around', 'give me a tour', 'how does this work'],
    reply: {
      text: "Yay — let's explore! I'll walk you through CNTRL, section by section. Ready?",
      actions: [A.tour],
      confident: true,
    },
  },
];

// ── The conversational tour ───────────────────────────────────────────────────

export interface TourStep {
  text: string;
  actions?: AgentAction[];
}

/** Each step explains a section with a "Show me" deep-link + "Next". */
export const TOUR_STEPS: TourStep[] = [
  {
    text: "🎬 **Welcome to the tour!** CNTRL is your all-in-one home for social media, PR and reporting. I'll keep each stop short — tap **Next** to move on, or **Show me** to jump in.",
  },
  {
    text: "**1. Home** — your starting point. A friendly greeting, quick action cards for every job, your tasks, and a glance at your numbers.",
    actions: [{ label: 'Show me', to: '/dashboard' }],
  },
  {
    text: "**2. Content Planner & Composer** — plan, write and schedule posts to all your networks at once, with per-network tweaks and approvals.",
    actions: [{ label: 'Show me', to: '/publish' }],
  },
  {
    text: "**3. Content Calendar** — everything you've scheduled, drag-and-drop to reschedule, tap to edit or publish.",
    actions: [{ label: 'Show me', to: '/calendar' }],
  },
  {
    text: "**4. Engage** — one inbox for comments, DMs, mentions and reviews, so you reply in one place.",
    actions: [{ label: 'Show me', to: '/engage' }],
  },
  {
    text: "**5. Listening** — track what people say about you, with sentiment and share of voice.",
    actions: [{ label: 'Show me', to: '/listening' }],
  },
  {
    text: "**6. Analytics & Reports** — see how you're performing and turn it into shareable PDF reports.",
    actions: [{ label: 'Analytics', to: '/analytics' }, { label: 'Reports', to: '/reports' }],
  },
  {
    text: "**7. And more** — SEO & GEO, Public Relations, CRM, Projects, Task Planner and Team Chat are all in the left menu. 🎉 That's the tour! Ask me about any of them, or switch to **Domina AI** when you want to *generate* content.",
    actions: [{ label: '👑 Try Domina AI', kind: 'ai' }, { label: 'Connect an account', ask: 'connect a social account' }],
  },
];

// ── Matching ──────────────────────────────────────────────────────────────────

const FALLBACK: AgentReply = {
  text: "Hmm, I'm not 100% sure I got that 🤔 — I'm a hard-wired helper, so I'm best with questions about using CNTRL (scheduling, Engage, Analytics, connecting accounts, and so on). Try rephrasing, take a tour, or I can hand you to a human or to **Domina AI**.",
  actions: [A.tour, A.human, A.ai],
  confident: false,
};

function scoreIntent(intent: Intent, normQuery: string, tokens: string[]): number {
  let score = 0;
  // Phrase (substring) hits are strong signals.
  for (const p of intent.phrases ?? []) {
    if (normQuery.includes(p)) score += 4;
  }
  // Keyword hits (fuzzy, token-level).
  for (const kw of intent.keywords) {
    if (kw.includes(' ')) {
      if (normQuery.includes(kw)) score += 3;
    } else if (tokens.some((t) => fuzzyEq(t, kw))) {
      score += 2;
    }
  }
  return score * (intent.weight ?? 1);
}

/** The Agent's answer for a free-text query. */
export function answerAgent(query: string): AgentReply {
  const norm = normalize(query);
  const tokens = tokensOf(query);
  if (tokens.length === 0) return FALLBACK;

  let best: Intent | null = null;
  let bestScore = 0;
  for (const intent of INTENTS) {
    const s = scoreIntent(intent, norm, tokens);
    if (s > bestScore) {
      bestScore = s;
      best = intent;
    }
  }

  // Require a minimum confidence so we don't force a wrong answer.
  if (best && bestScore >= 2) return best.reply;
  return FALLBACK;
}

/** Quick-start chips shown before the user types anything. */
export const QUICK_STARTS: AgentAction[] = [
  { label: '✨ Take a tour', kind: 'tour' },
  { label: '📅 How do I schedule a post?', ask: 'how do I schedule a post' },
  { label: '🔗 Connect an account', ask: 'connect a social account' },
  { label: '👑 What can Domina AI do?', ask: 'what can domina ai generate' },
  { label: '🙋 Talk to a human', kind: 'human' },
];
